import type { FamilleRepository } from '@taki/sdk';

import type { FicheGenerique } from './app-declaree.js';

/**
 * LA TAILLE D'UNE PAGE DE LECTURE, alignee sur le plafond de page du SDK
 * (`MAX_LIST_LIMIT`, `packages/sdk/src/list-query.ts`).
 */
export const PAGE_DE_CHARGEMENT = 1000;

/**
 * LE JEU ENTIER, LU PAR PAGES (chantier `ecran-fluide`). L'ecran reste en
 * pagination CLIENTE : il charge toujours tout le jeu, et la recherche, les
 * filtres, les stats et les compteurs portent sur tout. Seule la LECTURE est
 * decoupee, par le curseur du SDK (`after`, l'id de la derniere fiche lue).
 *
 * POURQUOI. D'un seul tenant, une requete de 10 000 fiches ne revenait jamais
 * sur la pile locale (le flux de l'emulateur tombe en erreur et se relance sans
 * fin) : la porte restait vide. Par pages de 1 000, le meme jeu se lit
 * (mesures du chantier, `docs/socle/ecran-fluide.md`). L'ordre est celui d'avant
 * (l'identifiant du document, l'ordre naturel d'une requete sans tri), et une
 * page de moins de 1 000 fiches dit la fin du jeu : un jeu court se lit
 * toujours en UNE requete, exactement comme avant.
 *
 * GARDE-FOU : un depot qui ignorerait le curseur rendrait toujours la meme
 * page. Une fiche deja lue n'est jamais rendue deux fois, et une page qui
 * n'apporte aucune fiche nouvelle arrete la lecture.
 */
export async function lireParPages(
  depot: Pick<FamilleRepository, 'list'>,
  options: { type?: string; archived: 'all' | 'exclude' },
): Promise<FicheGenerique[]> {
  const fiches: FicheGenerique[] = [];
  const vues = new Set<string>();
  let apres: string | undefined;
  for (;;) {
    const page = await depot.list({ ...options, limit: PAGE_DE_CHARGEMENT, ...(apres === undefined ? {} : { after: apres }) });
    let nouvelles = 0;
    for (const fiche of page) {
      const id = fiche['id'];
      if (typeof id === 'string' && id !== '') {
        if (vues.has(id)) continue;
        vues.add(id);
      }
      fiches.push(fiche);
      nouvelles += 1;
    }
    if (page.length < PAGE_DE_CHARGEMENT) return fiches;
    const derniere = page[page.length - 1]?.['id'];
    if (nouvelles === 0 || typeof derniere !== 'string' || derniere === '' || derniere === apres) return fiches;
    apres = derniere;
  }
}
